import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminArticles, adminTestimonials, adminAppointments, type AdminArticle, type AdminTestimonial, type AdminAppointment } from '../../../mocks/admin-data';

const LOGO_URL = 'https://static.readdy.ai/image/63ce3e67fd2d5effcfb662b2683f46ed/b9e0099c33a19326b8165990727f4d26.png';
const categories = ['Aile', 'İlişkiler', 'Çocuk & Ergen', 'Psikoloji'];
const statusColors: Record<string, string> = {
  'Yeni': 'bg-blue-50 text-blue-600',
  'İletişime Geçildi': 'bg-amber-50 text-amber-600',
  'Tamamlandı': 'bg-green-50 text-green-600',
  'Yayında': 'bg-green-50 text-green-600',
  'Taslak': 'bg-gray-100 text-gray-500',
  'Aktif': 'bg-green-50 text-green-600',
  'Gizli': 'bg-gray-100 text-gray-500',
};

type Section = 'dashboard' | 'makaleler' | 'yorumlar' | 'randevular';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [section, setSection] = useState<Section>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [articles, setArticles] = useState<AdminArticle[]>(adminArticles);
  const [testimonials, setTestimonials] = useState<AdminTestimonial[]>(adminTestimonials);
  const [appointments, setAppointments] = useState<AdminAppointment[]>(adminAppointments);
  const [articleModal, setArticleModal] = useState(false);
  const [testimonialModal, setTestimonialModal] = useState(false);
  const [editArticle, setEditArticle] = useState<AdminArticle | null>(null);
  const [editTestimonial, setEditTestimonial] = useState<AdminTestimonial | null>(null);
  const [articleForm, setArticleForm] = useState({ title: '', category: 'Aile', status: 'Taslak' as 'Taslak' | 'Yayında', publishDate: '' });
  const [testimonialForm, setTestimonialForm] = useState({ name: '', comment: '', rating: 5, service: '', status: 'Aktif' as 'Aktif' | 'Gizli' });

  const navItems: { key: Section; icon: string; label: string }[] = [
    { key: 'dashboard', icon: 'ri-dashboard-line', label: 'Dashboard' },
    { key: 'makaleler', icon: 'ri-article-line', label: 'Makaleler' },
    { key: 'yorumlar', icon: 'ri-chat-3-line', label: 'Yorumlar' },
    { key: 'randevular', icon: 'ri-calendar-line', label: 'Randevular' },
  ];

  const openArticleAdd = () => { setEditArticle(null); setArticleForm({ title: '', category: 'Aile', status: 'Taslak', publishDate: '' }); setArticleModal(true); };
  const openArticleEdit = (a: AdminArticle) => { setEditArticle(a); setArticleForm({ title: a.title, category: a.category, status: a.status, publishDate: a.publishDate }); setArticleModal(true); };
  const saveArticle = () => {
    if (!articleForm.title) return;
    if (editArticle) {
      setArticles(prev => prev.map(a => a.id === editArticle.id ? { ...a, ...articleForm } : a));
    } else {
      setArticles(prev => [...prev, { id: Date.now(), ...articleForm, image: '' }]);
    }
    setArticleModal(false);
  };
  const deleteArticle = (id: number) => setArticles(prev => prev.filter(a => a.id !== id));

  const openTestimonialAdd = () => { setEditTestimonial(null); setTestimonialForm({ name: '', comment: '', rating: 5, service: '', status: 'Aktif' }); setTestimonialModal(true); };
  const openTestimonialEdit = (t: AdminTestimonial) => { setEditTestimonial(t); setTestimonialForm({ name: t.name, comment: t.comment, rating: t.rating, service: t.service, status: t.status }); setTestimonialModal(true); };
  const saveTestimonial = () => {
    if (!testimonialForm.name || !testimonialForm.comment) return;
    if (editTestimonial) {
      setTestimonials(prev => prev.map(t => t.id === editTestimonial.id ? { ...t, ...testimonialForm } : t));
    } else {
      setTestimonials(prev => [...prev, { id: Date.now(), ...testimonialForm }]);
    }
    setTestimonialModal(false);
  };
  const deleteTestimonial = (id: number) => setTestimonials(prev => prev.filter(t => t.id !== id));

  const updateAppointmentStatus = (id: number, status: AdminAppointment['status']) => {
    setAppointments(prev => prev.map(a => a.id === id ? { ...a, status } : a));
  };

  return (
    <div className="min-h-screen bg-[#F7F4EF] flex font-sans">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-56' : 'w-16'} flex-shrink-0 bg-warm-dark transition-all duration-300 flex flex-col`}>
        <div className="h-16 flex items-center px-4 border-b border-white/10">
          {sidebarOpen ? <img src={LOGO_URL} alt="Logo" className="h-8 w-auto object-contain brightness-0 invert opacity-80" /> : <i className="ri-leaf-line text-gold text-xl" />}
        </div>
        <nav className="flex-1 py-4 flex flex-col gap-1">
          {navItems.map(item => (
            <button key={item.key} onClick={() => setSection(item.key)}
              className={`flex items-center gap-3 px-4 py-3 mx-2 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap ${section === item.key ? 'bg-gold text-white' : 'text-warm-pale/60 hover:text-warm-pale hover:bg-white/5'}`}>
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className={`${item.icon} text-base`} /></span>
              {sidebarOpen && <span>{item.label}</span>}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button onClick={() => navigate('/admin')} className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-warm-pale/50 hover:text-warm-pale text-sm w-full cursor-pointer whitespace-nowrap`}>
            <span className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className="ri-logout-box-line text-base" /></span>
            {sidebarOpen && <span>Çıkış</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 bg-white border-b border-cream-deeper flex items-center justify-between px-6 flex-shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="w-8 h-8 flex items-center justify-center cursor-pointer text-warm-mid hover:text-warm-dark">
              <i className="ri-menu-line text-base" />
            </button>
            <h1 className="font-serif text-lg text-warm-dark capitalize">{section === 'dashboard' ? 'Dashboard' : navItems.find(n => n.key === section)?.label}</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-warm-light">Uzm. Ayşegül Sert</span>
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gold text-white text-xs font-semibold">AŞ</div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Dashboard */}
          {section === 'dashboard' && (
            <div>
              <p className="text-warm-mid text-sm mb-6">Hoş geldiniz! İşte genel bakışınız.</p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {[
                  { icon: 'ri-article-line', label: 'Makaleler', value: articles.length, color: 'text-amber-500' },
                  { icon: 'ri-chat-3-line', label: 'Yorumlar', value: testimonials.length, color: 'text-green-500' },
                  { icon: 'ri-calendar-line', label: 'Randevular', value: appointments.length, color: 'text-blue-500' },
                  { icon: 'ri-time-line', label: 'Yeni Randevu', value: appointments.filter(a => a.status === 'Yeni').length, color: 'text-red-400' },
                ].map(stat => (
                  <div key={stat.label} className="bg-white rounded-xl border border-cream-deeper p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className={`w-8 h-8 flex items-center justify-center`}><i className={`${stat.icon} ${stat.color} text-xl`} /></span>
                      <span className="font-serif text-2xl text-warm-dark font-semibold">{stat.value}</span>
                    </div>
                    <p className="text-xs text-warm-light">{stat.label}</p>
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-xl border border-cream-deeper p-5">
                <h3 className="font-serif text-base text-warm-dark mb-4">Son Randevular</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-cream-deeper">{['Ad Soyad','Konu','Tarih','Durum'].map(h=><th key={h} className="text-left py-2 px-3 text-xs font-medium text-warm-light">{h}</th>)}</tr></thead>
                    <tbody>{appointments.slice(0,3).map(a=><tr key={a.id} className="border-b border-cream-deeper/50 last:border-0"><td className="py-2.5 px-3 text-warm-dark text-xs">{a.name}</td><td className="py-2.5 px-3 text-warm-mid text-xs">{a.topic}</td><td className="py-2.5 px-3 text-warm-mid text-xs">{a.date}</td><td className="py-2.5 px-3"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[a.status]}`}>{a.status}</span></td></tr>)}</tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Articles */}
          {section === 'makaleler' && (
            <div>
              <div className="flex items-center justify-between mb-5">
                <p className="text-sm text-warm-mid">{articles.length} makale</p>
                <button onClick={openArticleAdd} className="px-4 py-2 bg-gold text-white text-xs font-medium rounded-lg hover:bg-gold-dark transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                  <i className="ri-add-line" /> Yeni Makale
                </button>
              </div>
              <div className="bg-white rounded-xl border border-cream-deeper overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-cream border-b border-cream-deeper">{['Başlık','Kategori','Durum','Tarih','İşlemler'].map(h=><th key={h} className="text-left py-3 px-4 text-xs font-medium text-warm-light">{h}</th>)}</tr></thead>
                    <tbody>{articles.map(a=>(
                      <tr key={a.id} className="border-b border-cream-deeper/50 last:border-0 hover:bg-cream/30">
                        <td className="py-3 px-4 text-warm-dark text-xs font-medium max-w-xs truncate">{a.title}</td>
                        <td className="py-3 px-4 text-warm-mid text-xs">{a.category}</td>
                        <td className="py-3 px-4"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[a.status]}`}>{a.status}</span></td>
                        <td className="py-3 px-4 text-warm-mid text-xs">{a.publishDate}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <button onClick={() => openArticleEdit(a)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-cream cursor-pointer"><i className="ri-edit-line text-warm-mid text-sm" /></button>
                            <button onClick={() => deleteArticle(a.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 cursor-pointer"><i className="ri-delete-bin-line text-red-400 text-sm" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Testimonials */}
          {section === 'yorumlar' && (
            <div>
              <div className="flex items-center justify-between mb-5">
                <p className="text-sm text-warm-mid">{testimonials.length} yorum</p>
                <button onClick={openTestimonialAdd} className="px-4 py-2 bg-gold text-white text-xs font-medium rounded-lg hover:bg-gold-dark transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                  <i className="ri-add-line" /> Yeni Yorum
                </button>
              </div>
              <div className="bg-white rounded-xl border border-cream-deeper overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-cream border-b border-cream-deeper">{['İsim','Hizmet','Puan','Durum','İşlemler'].map(h=><th key={h} className="text-left py-3 px-4 text-xs font-medium text-warm-light">{h}</th>)}</tr></thead>
                    <tbody>{testimonials.map(t=>(
                      <tr key={t.id} className="border-b border-cream-deeper/50 last:border-0 hover:bg-cream/30">
                        <td className="py-3 px-4 text-warm-dark text-xs font-medium">{t.name}</td>
                        <td className="py-3 px-4 text-warm-mid text-xs">{t.service}</td>
                        <td className="py-3 px-4"><div className="flex gap-0.5">{Array.from({length:t.rating}).map((_,i)=><i key={i} className="ri-star-fill text-gold text-xs" />)}</div></td>
                        <td className="py-3 px-4"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[t.status]}`}>{t.status}</span></td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <button onClick={() => openTestimonialEdit(t)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-cream cursor-pointer"><i className="ri-edit-line text-warm-mid text-sm" /></button>
                            <button onClick={() => deleteTestimonial(t.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 cursor-pointer"><i className="ri-delete-bin-line text-red-400 text-sm" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Appointments */}
          {section === 'randevular' && (
            <div>
              <p className="text-sm text-warm-mid mb-5">{appointments.length} randevu talebi</p>
              <div className="bg-white rounded-xl border border-cream-deeper overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-cream border-b border-cream-deeper">{['Ad Soyad','Telefon','Konu','Tarih & Saat','Tür','Durum'].map(h=><th key={h} className="text-left py-3 px-4 text-xs font-medium text-warm-light">{h}</th>)}</tr></thead>
                    <tbody>{appointments.map(a=>(
                      <tr key={a.id} className="border-b border-cream-deeper/50 last:border-0 hover:bg-cream/30">
                        <td className="py-3 px-4 text-warm-dark text-xs font-medium">{a.name}</td>
                        <td className="py-3 px-4 text-warm-mid text-xs">{a.phone}</td>
                        <td className="py-3 px-4 text-warm-mid text-xs">{a.topic}</td>
                        <td className="py-3 px-4 text-warm-mid text-xs">{a.date} {a.time}</td>
                        <td className="py-3 px-4 text-warm-mid text-xs">{a.type}</td>
                        <td className="py-3 px-4">
                          <select value={a.status} onChange={e => updateAppointmentStatus(a.id, e.target.value as AdminAppointment['status'])}
                            className={`text-xs px-2 py-1 rounded-full font-medium border-0 cursor-pointer focus:outline-none ${statusColors[a.status]}`}>
                            {['Yeni','İletişime Geçildi','Tamamlandı'].map(s=><option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Article Modal */}
      {articleModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setArticleModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="font-serif text-lg text-warm-dark mb-4">{editArticle ? 'Makale Düzenle' : 'Yeni Makale'}</h3>
            <div className="flex flex-col gap-3">
              <div><label className="text-xs font-medium text-warm-mid mb-1 block">Başlık *</label><input value={articleForm.title} onChange={e=>setArticleForm(f=>({...f,title:e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-cream-deeper text-sm focus:outline-none focus:border-gold" /></div>
              <div><label className="text-xs font-medium text-warm-mid mb-1 block">Kategori</label><select value={articleForm.category} onChange={e=>setArticleForm(f=>({...f,category:e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-cream-deeper text-sm focus:outline-none focus:border-gold cursor-pointer">{categories.map(c=><option key={c}>{c}</option>)}</select></div>
              <div><label className="text-xs font-medium text-warm-mid mb-1 block">Durum</label><select value={articleForm.status} onChange={e=>setArticleForm(f=>({...f,status:e.target.value as 'Taslak'|'Yayında'}))} className="w-full px-3 py-2 rounded-lg border border-cream-deeper text-sm focus:outline-none focus:border-gold cursor-pointer"><option>Taslak</option><option>Yayında</option></select></div>
              <div><label className="text-xs font-medium text-warm-mid mb-1 block">Yayın Tarihi</label><input type="text" value={articleForm.publishDate} onChange={e=>setArticleForm(f=>({...f,publishDate:e.target.value}))} placeholder="GG.AA.YYYY" className="w-full px-3 py-2 rounded-lg border border-cream-deeper text-sm focus:outline-none focus:border-gold" /></div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setArticleModal(false)} className="flex-1 py-2.5 border border-cream-deeper text-warm-mid text-sm rounded-lg cursor-pointer whitespace-nowrap hover:border-warm-mid">İptal</button>
              <button onClick={saveArticle} className="flex-1 py-2.5 bg-gold text-white text-sm rounded-lg cursor-pointer whitespace-nowrap hover:bg-gold-dark">Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* Testimonial Modal */}
      {testimonialModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setTestimonialModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="font-serif text-lg text-warm-dark mb-4">{editTestimonial ? 'Yorum Düzenle' : 'Yeni Yorum'}</h3>
            <div className="flex flex-col gap-3">
              <div><label className="text-xs font-medium text-warm-mid mb-1 block">İsim *</label><input value={testimonialForm.name} onChange={e=>setTestimonialForm(f=>({...f,name:e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-cream-deeper text-sm focus:outline-none focus:border-gold" /></div>
              <div><label className="text-xs font-medium text-warm-mid mb-1 block">Hizmet</label><input value={testimonialForm.service} onChange={e=>setTestimonialForm(f=>({...f,service:e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-cream-deeper text-sm focus:outline-none focus:border-gold" /></div>
              <div><label className="text-xs font-medium text-warm-mid mb-1 block">Yorum *</label><textarea value={testimonialForm.comment} onChange={e=>setTestimonialForm(f=>({...f,comment:e.target.value}))} rows={3} maxLength={500} className="w-full px-3 py-2 rounded-lg border border-cream-deeper text-sm focus:outline-none focus:border-gold resize-none" /></div>
              <div><label className="text-xs font-medium text-warm-mid mb-1 block">Puan</label><select value={testimonialForm.rating} onChange={e=>setTestimonialForm(f=>({...f,rating:Number(e.target.value)}))} className="w-full px-3 py-2 rounded-lg border border-cream-deeper text-sm focus:outline-none focus:border-gold cursor-pointer">{[5,4,3,2,1].map(n=><option key={n} value={n}>{n} Yıldız</option>)}</select></div>
              <div><label className="text-xs font-medium text-warm-mid mb-1 block">Durum</label><select value={testimonialForm.status} onChange={e=>setTestimonialForm(f=>({...f,status:e.target.value as 'Aktif'|'Gizli'}))} className="w-full px-3 py-2 rounded-lg border border-cream-deeper text-sm focus:outline-none focus:border-gold cursor-pointer"><option>Aktif</option><option>Gizli</option></select></div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setTestimonialModal(false)} className="flex-1 py-2.5 border border-cream-deeper text-warm-mid text-sm rounded-lg cursor-pointer whitespace-nowrap hover:border-warm-mid">İptal</button>
              <button onClick={saveTestimonial} className="flex-1 py-2.5 bg-gold text-white text-sm rounded-lg cursor-pointer whitespace-nowrap hover:bg-gold-dark">Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}